(() => {
  if (window.__rnfHomeCollage || !window.matchMedia) return;
  window.__rnfHomeCollage = true;
  const desktop = window.matchMedia('(min-width: 990px)');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pairs = new Map();
  const entered = new WeakSet();

  function mountMotion(pair) {
    const { first, second, wrapper } = pair;
    const photo = second.querySelector('.ftm-media-box > img');
    const media = second.querySelector('.ftm-media');
    const copy = first.querySelector('.rnf-overlap-grid__text');
    const button = second.querySelector('.ftm-content');
    const car = first.querySelector('.rnf-overlap__image--car');
    const watch = first.querySelector('.rnf-overlap__image--watch');
    if (!photo || !media || !photo.animate) return;
    let phase = 'ready';
    let visible = false;
    let frame = 0;
    let lastY = '';
    let animations = [];
    let observer;
    wrapper.dataset.rnfCollageMotion = 'ready';

    function settle() {
      if (phase === 'disposed') return;
      phase = 'settled';
      entered.add(first);
      wrapper.dataset.rnfCollageMotion = 'settled';
      animations.forEach(animation => animation.cancel());
      animations = [];
      observer?.unobserve(media);
      schedule();
    }

    function update() {
      frame = 0;
      if (phase !== 'settled' || !visible || document.hidden || reduced.matches || !watch) return;
      // Measure the stationary card group, never the translated watch itself.
      const rect = watch.parentElement.getBoundingClientRect();
      const height = window.innerHeight || document.documentElement.clientHeight;
      const center = rect.top + watch.offsetTop + watch.offsetHeight / 2;
      const y = Math.max(-28, Math.min(28, (center - height / 2) / height * 40));
      const value = `${Math.round(y * 10) / 10}px`;
      if (value === lastY) return;
      watch.style.setProperty('--rnf-collage-y', value);
      lastY = value;
    }

    function schedule() {
      if (!frame && phase === 'settled' && visible && !document.hidden && !reduced.matches) frame = requestAnimationFrame(update);
    }

    async function play() {
      if (phase !== 'ready') return;
      phase = 'loading';
      try { if (photo.decode) await photo.decode(); } catch (_) { /* A failed image must never hide the rest of the content. */ }
      if (phase !== 'loading') return;
      if (!desktop.matches || reduced.matches || document.hidden || !wrapper.isConnected) { settle(); return; }
      phase = 'playing';
      wrapper.dataset.rnfCollageMotion = 'playing';
      const ease = 'cubic-bezier(.18,.92,.18,1)';
      const animate = (element, frames, duration, delay = 0) => {
        if (element) animations.push(element.animate(frames, { duration, delay, easing: ease, fill: 'both' }));
      };
      try {
        animate(photo, [{ transform: 'translateY(100%)' }, { transform: 'translateY(0)' }], 1100);
        [copy, button].forEach(element => animate(element, [{ opacity: 0, transform: 'translateY(38px)' }, { opacity: 1, transform: 'translateY(0)' }], 1100));
        animate(car, [{ opacity: 0, transform: 'translateX(-190px) rotate(-22deg)' }, { opacity: 1, transform: 'translateX(0) rotate(-6deg)' }], 1350, 850);
        animate(watch, [{ opacity: 0, transform: 'translateX(190px) rotate(24deg)' }, { opacity: 1, transform: 'translateX(0) rotate(8deg)' }], 1450, 1700);
        Promise.all(animations.map(animation => animation.finished)).then(settle, () => {});
      } catch (_) { settle(); }
    }

    function visibilityChanged() {
      if (document.hidden) {
        cancelAnimationFrame(frame);
        frame = 0;
        if (phase === 'playing' || phase === 'loading') settle();
      } else schedule();
    }

    function motionChanged() {
      if (reduced.matches) {
        cancelAnimationFrame(frame);
        frame = 0;
        settle();
        watch?.style.removeProperty('--rnf-collage-y');
        lastY = '';
      } else schedule();
    }

    if (window.IntersectionObserver) {
      observer = new window.IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.target === watch) visible = entry.isIntersecting;
          if (entry.target === media && entry.isIntersecting && entry.intersectionRatio >= .18) play();
        });
        schedule();
      }, { threshold: [0, .18] });
      observer.observe(media);
      if (watch) observer.observe(watch);
    } else settle();
    if (entered.has(first) || reduced.matches) settle();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('visibilitychange', visibilityChanged);
    if (reduced.addEventListener) reduced.addEventListener('change', motionChanged);
    else reduced.addListener(motionChanged);
    pair.disposeMotion = () => {
      // A resized or reloaded section stays visible; entrance only runs on the first visit.
      entered.add(first);
      phase = 'disposed';
      cancelAnimationFrame(frame);
      animations.forEach(animation => animation.cancel());
      observer?.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('visibilitychange', visibilityChanged);
      if (reduced.removeEventListener) reduced.removeEventListener('change', motionChanged);
      else reduced.removeListener(motionChanged);
      watch?.style.removeProperty('--rnf-collage-y');
      delete wrapper.dataset.rnfCollageMotion;
    };
  }

  function unwrap(pair) {
    const { first, second, wrapper } = pair;
    pair.titleObserver?.disconnect();
    pair.disposeMotion?.();
    if (wrapper.parentNode) {
      [first, second].forEach(section => {
        if (section.parentNode === wrapper) wrapper.parentNode.insertBefore(section, wrapper);
      });
      wrapper.remove();
    }
    first.classList.remove('rnf-home-collage__lead');
    second.classList.remove('rnf-home-collage__follow');
    pairs.delete(first);
  }

  function mount() {
    [...pairs.values()].forEach(pair => {
      if (!desktop.matches || !pair.wrapper.isConnected || pair.first.parentNode !== pair.wrapper || pair.second.parentNode !== pair.wrapper) unwrap(pair);
    });
    if (!desktop.matches) return;
    document.querySelectorAll('[data-rnf-overlap-parallax]').forEach(section => {
      const first = section.closest('.shopify-section');
      if (!first || pairs.has(first) || first.closest('.rnf-home-collage')) return;
      const second = first.nextElementSibling;
      if (!second?.classList.contains('shopify-section') || !second.querySelector('.ftm-section')) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'rnf-home-collage';
      first.before(wrapper);
      first.classList.add('rnf-home-collage__lead');
      second.classList.add('rnf-home-collage__follow');
      wrapper.append(first, second);
      const pair = { first, second, wrapper };
      pairs.set(first, pair);
      mountMotion(pair);
      const titleSection = second.querySelector('.ftm-section');
      const titleContent = titleSection.querySelector('.ftm-content');
      if (titleContent && !titleSection.classList.contains('ftm-inview')) {
        if (window.IntersectionObserver) {
          pair.titleObserver = new window.IntersectionObserver(entries => {
            if (pairs.get(first) !== pair || !entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= .2)) return;
            titleSection.classList.add('ftm-inview');
            pair.titleObserver.disconnect();
          }, { threshold: .2, rootMargin: '0px' });
          pair.titleObserver.observe(titleContent);
        } else titleSection.classList.add('ftm-inview');
      }
    });
  }

  if (desktop.addEventListener) desktop.addEventListener('change', mount);
  else desktop.addListener(mount);
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', event => {
    [...pairs.values()].forEach(pair => {
      if (event.target.contains(pair.first) || event.target.contains(pair.second) || pair.first.contains(event.target) || pair.second.contains(event.target)) unwrap(pair);
    });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
})();

(() => {
  if (window.__rnfHomeSrtOrder || !window.matchMedia) return;
  window.__rnfHomeSrtOrder = true;
  const desktop = window.matchMedia('(min-width: 990px)');
  let placement;
  const find = key => document.querySelector(`.shopify-section[id$="__${key}"]`);

  function restore() {
    if (!placement) return;
    placement.items.forEach(({ section, marker }) => {
      if (!marker.isConnected) return;
      if (section.isConnected) marker.replaceWith(section);
      else marker.remove();
    });
    placement = undefined;
  }

  function arrange() {
    if (placement && (!desktop.matches || !placement.reviews.isConnected || placement.items.some(({ section, marker }) => !section.isConnected || !marker.isConnected))) restore();
    if (!desktop.matches || placement) return;
    const title = find('rich_text_wxVBPU');
    const strip = find('scrolling_images_ncaaxT');
    const reviews = find('rnf_reviews_slider_fixed_xU64GX');
    if (!title || !strip || !reviews || title.parentNode !== reviews.parentNode || strip.parentNode !== reviews.parentNode) return;
    const items = [title, strip].map(section => {
      const marker = document.createComment('rnf-srt-original-position');
      section.before(marker);
      return { section, marker };
    });
    reviews.after(title, strip);
    placement = { items, reviews };
  }

  if (desktop.addEventListener) desktop.addEventListener('change', arrange);
  else desktop.addListener(arrange);
  document.addEventListener('shopify:section:load', arrange);
  document.addEventListener('shopify:section:unload', event => {
    if (placement && [placement.reviews, ...placement.items.map(item => item.section)].some(section => event.target.contains(section) || section.contains(event.target))) restore();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arrange, { once: true });
  else arrange();
})();
